import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';
import { PLACEHOLDER_IMAGE, userProgressApi, raritiesApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';

const RARITY_STYLES = {
    Common: { bg: 'bg-gray-500/10', border: 'border-gray-500/30', text: 'text-gray-400' },
    Uncommon: { bg: 'bg-green-500/10', border: 'border-green-500/30', text: 'text-green-400' },
    Rare: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400' },
    Epic: { bg: 'bg-purple-500/10', border: 'border-purple-500/30', text: 'text-purple-400' },
    Legendary: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400' },
    Mythical: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400' },
    Monarch: { bg: 'bg-sky-500/10', border: 'border-sky-500/30', text: 'text-sky-400' },
    Limited: { bg: 'bg-pink-500/10', border: 'border-pink-500/30', text: 'text-pink-400' },
    ULimited: { bg: 'bg-violet-900/10', border: 'border-violet-900/30', text: 'text-violet-400' },
};

// Achievement Schwierigkeitsfarben
const DIFFICULTY_COLORS = {
    'Very Easy': 'rgb(61, 92, 61)',
    'Easy': 'rgb(45, 136, 46)',
    'Medium': 'rgb(139, 93, 0)',
    'Hard': 'rgb(177, 59, 59)',
    'Very Hard': 'rgb(88, 0, 133)',
    'Extrem': 'rgb(163, 181, 0)',
};

export const WikiCard = ({ entry, category, viewMode = 'grid' }) => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { language } = useLanguage();
    const [received, setReceived] = useState(false);
    const [selectedRankId, setSelectedRankId] = useState('');
    const [selectedRarity, setSelectedRarity] = useState('');
    const [currentLevel, setCurrentLevel] = useState(0);
    const [ranks, setRanks] = useState([]);
    const [rarities, setRarities] = useState([]);

    const isRelic = category === 'relics';
    const isAccessory = category === 'accessories';
    const isSkill = category === 'skills';
    const isGacha = category === 'gachas';
    const isUpgrade = category === 'upgrades';
    const isAchievement = category === 'achievements';
    const showReceivedCheckbox = isRelic || isAccessory || isSkill;

    useEffect(() => {
        loadOptions();
        loadProgress();
    }, [entry.id, category, user]);

    const loadOptions = async () => {
        try {
            if (isRelic && entry.ranks?.length > 0) {
                // Use ranks from the entry itself, with generated IDs if needed
                const entryRanks = entry.ranks.map((r, idx) => ({
                    id: r.id || `rank_${idx}`,
                    name: r.rank_name,
                    is_max: r.is_max || false,
                    fields: r.fields || []
                }));
                setRanks(entryRanks);
            } else if (isGacha) {
                const res = await raritiesApi.getAll();
                setRarities(res.data.rarities || []);
            }
        } catch (error) {
            console.error('Failed to load options:', error);
        }
    };

    const loadProgress = async () => {
        if (!user) return;
        try {
            const res = await userProgressApi.getItem(user.discord_id, entry.id);
            if (res.data && Object.keys(res.data).length > 0) {
                setReceived(res.data.received || false);
                setSelectedRankId(res.data.selected_rank_id || '');
                setSelectedRarity(res.data.selected_rarity || '');
                setCurrentLevel(res.data.current_level || 0);
            }
        } catch (error) {
            console.error('Failed to load progress:', error);
        }
    };

    const updateProgress = async (updates) => {
        if (!user) return;
        try {
            await userProgressApi.updateItem(user.discord_id, entry.id, category, updates);
        } catch (error) {
            console.error('Failed to update progress:', error);
        }
    };

    const handleReceivedChange = async (e) => {
        e.stopPropagation();
        const newValue = !received;
        setReceived(newValue);
        updateProgress({ received: newValue });
    };

    const handleRankChange = (value) => {
        setSelectedRankId(value);
        updateProgress({ selected_rank_id: value });
    };

    const handleRarityChange = (value) => {
        setSelectedRarity(value);
        updateProgress({ selected_rarity: value });
    };

    const handleLevelChange = (value) => {
        const level = parseInt(value) || 0;
        setCurrentLevel(level);
        updateProgress({ current_level: level });
    };

    const handleClick = () => {
        navigate(`/${category}/${entry.id}`);
    };

    const rarityStyle = entry.rarity ? RARITY_STYLES[entry.rarity] : null;
    const imageUrl = entry.image_url || PLACEHOLDER_IMAGE;

    // Get available rarities for this gacha (those that have values defined)
    const availableRarities = isGacha && entry.values_by_rarity 
        ? entry.values_by_rarity.filter(v => v.fields?.length > 0).map(v => v.rarity)
        : [];

    // Get available rarities for achievements
    const availableAchievementRarities = isAchievement && entry.values_by_rarity 
        ? entry.values_by_rarity.filter(v => v.description || v.rewards).map(v => v.rarity)
        : [];

    // Get available levels for upgrade (from level_values)
    const availableLevels = isUpgrade && entry.level_values 
        ? entry.level_values.map(v => v.level)
        : [];

    // List View
    if (viewMode === 'list') {
        return (
            <div
                data-testid={`wiki-card-${entry.id}`}
                className={cn(
                    "flex items-center gap-4 p-3 rounded-xl cursor-pointer",
                    "bg-card dark:bg-card light:bg-white border border-white/5 dark:border-white/5 light:border-gray-200",
                    "hover:border-cyan-500/30 transition-all duration-200",
                    received && showReceivedCheckbox && "ring-1 ring-green-500/50 border-green-500/30"
                )}
                onClick={handleClick}
            >
                {/* Image */}
                <div className="w-14 h-14 rounded-lg overflow-hidden shrink-0 bg-white/5">
                    <img
                        src={imageUrl}
                        alt={entry.title}
                        className="w-full h-full object-cover"
                    />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <h3 className="font-['Rajdhani'] font-semibold text-white dark:text-white light:text-gray-900 truncate">
                            {entry.title}
                        </h3>
                        {entry.is_meta && (
                            <span className="px-1.5 py-0.5 rounded bg-orange-500 text-black text-[10px] font-bold">META</span>
                        )}
                        {rarityStyle && (
                            <span className={cn(
                                "px-1.5 py-0.5 rounded text-[10px] font-mono uppercase border shrink-0",
                                rarityStyle.bg, rarityStyle.border, rarityStyle.text
                            )}>
                                {entry.rarity}
                            </span>
                        )}
                    </div>
                    {entry.description && (
                        <p className="text-sm text-gray-500 truncate">{entry.description}</p>
                    )}
                </div>

                {/* Received Indicator */}
                {showReceivedCheckbox && (
                    <div 
                        className={cn(
                            "w-6 h-6 rounded-full flex items-center justify-center shrink-0",
                            received ? "bg-green-500" : "bg-white/10"
                        )}
                        onClick={(e) => { e.stopPropagation(); handleReceivedChange(e); }}
                    >
                        {received && <Check size={14} className="text-black" />}
                    </div>
                )}

                <ChevronRight size={20} className="text-gray-500 shrink-0" />
            </div>
        );
    }

    // Grid View (default)
    return (
        <div
            data-testid={`wiki-card-${entry.id}`}
            className={cn(
                "group relative overflow-hidden rounded-xl w-[250px]",
                "bg-card dark:bg-card light:bg-white border border-white/5 dark:border-white/5 light:border-gray-200",
                "transition-all duration-200",
                received && showReceivedCheckbox && "ring-2 ring-green-500/50 border-green-500/30"
            )}
        >
            {/* Image - Clickable */}
            <div 
                className="w-[250px] h-[250px] overflow-hidden cursor-pointer relative"
                onClick={handleClick}
            >
                <img
                    src={imageUrl}
                    alt={entry.title}
                    className="w-[250px] h-[250px] object-contain transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-card via-transparent to-transparent pointer-events-none" />
                
                {/* Relic Badge: Meta + Rarity */}
                {isRelic && (entry.is_meta || entry.rarity) && (
                    <div className="absolute top-3 left-3 flex gap-1">
                        {entry.is_meta && (
                            <div className="px-2 py-1 rounded bg-orange-500 text-black text-xs font-bold">
                                META
                            </div>
                        )}
                        {entry.rarity && rarityStyle && (
                            <div className={cn(
                                "px-2 py-1 rounded text-xs font-bold border",
                                rarityStyle.bg, rarityStyle.border, rarityStyle.text
                            )}>
                                {entry.rarity}
                            </div>
                        )}
                    </div>
                )}

                {/* Relic: Maxed Badge wenn ausgewählter Rang Maxed ist - RECHTS */}
                {isRelic && selectedRankId && (() => {
                    const selectedRank = ranks.find(r => r.id === selectedRankId);
                    return selectedRank?.is_max ? (
                        <div className="absolute top-3 right-3 px-2 py-1 rounded bg-yellow-500 text-black text-xs font-bold">
                            MAXED
                        </div>
                    ) : null;
                })()}

                {/* Gacha: Meta/Maxed Badge wenn ausgewählte Seltenheit diese hat */}
                {isGacha && selectedRarity && (() => {
                    const selectedRarityData = entry.values_by_rarity?.find(v => v.rarity === selectedRarity);
                    if (!selectedRarityData?.is_meta && !selectedRarityData?.is_maxed) return null;
                    return (
                        <div className="absolute top-3 left-3 flex gap-1">
                            {selectedRarityData?.is_meta && (
                                <div className="px-2 py-1 rounded bg-orange-500 text-black text-xs font-bold">
                                    META
                                </div>
                            )}
                            {selectedRarityData?.is_maxed && (
                                <div className="px-2 py-1 rounded bg-yellow-500 text-black text-xs font-bold">
                                    MAXED
                                </div>
                            )}
                        </div>
                    );
                })()}

                {/* Achievement: Maxed Badge wenn ausgewählte Seltenheit Maxed ist */}
                {isAchievement && selectedRarity && (() => {
                    const selectedRarityData = entry.values_by_rarity?.find(v => v.rarity === selectedRarity);
                    return selectedRarityData?.is_maxed ? (
                        <div className="absolute top-3 right-3 px-2 py-1 rounded bg-yellow-500 text-black text-xs font-bold">
                            MAXED
                        </div>
                    ) : null;
                })()}

                {/* Upgrade: Maxed Badge wenn ausgewählte Stufe Maxed ist - RECHTS */}
                {isUpgrade && currentLevel > 0 && (() => {
                    const selectedLevelData = entry.level_values?.find(v => v.level === currentLevel);
                    return selectedLevelData?.is_maxed ? (
                        <div className="absolute top-3 right-3 px-2 py-1 rounded bg-yellow-500 text-black text-xs font-bold">
                            MAXED
                        </div>
                    ) : null;
                })()}
            </div>

            {/* Content */}
            <div className="p-5 space-y-3">
                {/* Title - Clickable */}
                <div 
                    className="cursor-pointer"
                    onClick={handleClick}
                >
                    <h3 className="font-['Rajdhani'] font-semibold text-xl text-white dark:text-white light:text-gray-900 group-hover:text-cyan-400 transition-colors">
                        {entry.title}
                    </h3>
                </div>

                {/* User Controls */}
                <div className="pt-3 border-t border-white/5 dark:border-white/5 light:border-gray-100 space-y-3" onClick={(e) => e.stopPropagation()}>
                    {/* Received Checkbox - für Relics, Accessories, Skills */}
                    {showReceivedCheckbox && (
                        <div className="flex items-center gap-3">
                            <label className="flex items-center gap-2 cursor-pointer group/check shrink-0">
                                <div className={cn(
                                    "w-5 h-5 rounded border-2 flex items-center justify-center transition-colors",
                                    received 
                                        ? "bg-green-500 border-green-500" 
                                        : "border-gray-600 group-hover/check:border-gray-400"
                                )}>
                                    {received && <Check size={12} className="text-black" />}
                                </div>
                                <input 
                                    type="checkbox"
                                    checked={received}
                                    onChange={handleReceivedChange}
                                    className="sr-only"
                                />
                                <span className={cn(
                                    "text-sm",
                                    received ? "text-green-400" : "text-gray-500"
                                )}>
                                    {language === 'de' ? 'Erhalten' : 'Received'}
                                </span>
                            </label>

                            {/* Rank Dropdown - nur für Relics */}
                            {isRelic && ranks.length > 0 && (
                                <Select value={selectedRankId || "none"} onValueChange={(v) => handleRankChange(v === "none" ? "" : v)}>
                                    <SelectTrigger className="h-8 bg-black/40 dark:bg-black/40 light:bg-gray-50 border-white/10 dark:border-white/10 light:border-gray-200 text-sm flex-1 min-w-0">
                                        <SelectValue placeholder={language === 'de' ? 'Rang wählen' : 'Select rank'} />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#0A0A0A] dark:bg-[#0A0A0A] light:bg-white border-white/10 dark:border-white/10 light:border-gray-200">
                                        <SelectItem value="none">{language === 'de' ? 'Kein Rang' : 'No rank'}</SelectItem>
                                        {ranks.map((rank) => (
                                            <SelectItem key={rank.id} value={rank.id}>
                                                {rank.name} {rank.is_max && '(MAX)'}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            )}
                        </div>
                    )}

                    {/* Rarity Dropdown - für Gachas */}
                    {isGacha && availableRarities.length > 0 && (
                        <Select value={selectedRarity || "none"} onValueChange={(v) => handleRarityChange(v === "none" ? "" : v)}>
                            <SelectTrigger className="h-9 bg-black/40 dark:bg-black/40 light:bg-gray-50 border-white/10 dark:border-white/10 light:border-gray-200 text-sm">
                                <SelectValue placeholder={language === 'de' ? 'Seltenheit wählen' : 'Select rarity'} />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0A0A0A] dark:bg-[#0A0A0A] light:bg-white border-white/10 dark:border-white/10 light:border-gray-200">
                                <SelectItem value="none">{language === 'de' ? 'Keine Seltenheit' : 'No rarity'}</SelectItem>
                                {availableRarities.map((rarity) => (
                                    <SelectItem key={rarity} value={rarity}>
                                        <span className={RARITY_STYLES[rarity]?.text}>{rarity}</span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}

                    {/* Rarity Selector - für Achievements */}
                    {isAchievement && availableAchievementRarities.length > 0 && (
                        <div className="space-y-2">
                            <label className="text-xs text-gray-500">{language === 'de' ? 'Aktuelle Stufe:' : 'Current tier:'}</label>
                            <Select value={selectedRarity} onValueChange={handleRarityChange}>
                                <SelectTrigger className="h-9 bg-black/40 dark:bg-black/40 light:bg-gray-50 border-white/10 dark:border-white/10 light:border-gray-200 text-sm">
                                    <SelectValue placeholder={language === 'de' ? 'Stufe wählen' : 'Select tier'} />
                                </SelectTrigger>
                                <SelectContent className="bg-[#0A0A0A] dark:bg-[#0A0A0A] light:bg-white border-white/10 dark:border-white/10 light:border-gray-200">
                                    <SelectItem value="none">{language === 'de' ? 'Keine' : 'None'}</SelectItem>
                                    {entry.values_by_rarity?.filter(v => v.description || v.rewards).map((tier) => (
                                        <SelectItem key={tier.rarity} value={tier.rarity}>
                                            <span className="flex items-center gap-2">
                                                <span 
                                                    className="w-3 h-3 rounded-full" 
                                                    style={{ backgroundColor: DIFFICULTY_COLORS[tier.difficulty] || 'rgb(139, 93, 0)' }}
                                                />
                                                {tier.rarity}
                                            </span>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}

                    {/* Level Selector - für Upgrades */}
                    {isUpgrade && availableLevels.length > 0 && (
                        <div className="space-y-2">
                            <label className="text-xs text-gray-500">{language === 'de' ? 'Aktuelles Level:' : 'Current level:'}</label>
                            <Select value={String(currentLevel)} onValueChange={handleLevelChange}>
                                <SelectTrigger className="h-9 bg-black/40 dark:bg-black/40 light:bg-gray-50 border-white/10 dark:border-white/10 light:border-gray-200 text-sm">
                                    <SelectValue placeholder={language === 'de' ? 'Level wählen' : 'Select level'} />
                                </SelectTrigger>
                                <SelectContent className="bg-[#0A0A0A] dark:bg-[#0A0A0A] light:bg-white border-white/10 dark:border-white/10 light:border-gray-200">
                                    <SelectItem value="0">{language === 'de' ? 'Nicht freigeschaltet' : 'Not unlocked'}</SelectItem>
                                    {availableLevels.map((level) => (
                                        <SelectItem key={level} value={String(level)}>
                                            Level {level}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
